import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../supabaseClient';

// ─── Constants ─────────────────────────────────────────────────────────────────

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };

// ─── Helpers ───────────────────────────────────────────────────────────────────

function timeToMinutes(str = '') {
  const m = str.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return 9999;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const period = m[3].toUpperCase();
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return h * 60 + min;
}

// ─── Toggle switch ─────────────────────────────────────────────────────────────

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className="relative inline-flex items-center flex-shrink-0 focus:outline-none transition-colors duration-300"
      style={{
        width: 36,
        height: 20,
        borderRadius: 10,
        backgroundColor: checked ? '#F7941D' : 'rgba(255,255,255,0.12)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        className="inline-block rounded-full bg-white shadow transition-transform duration-300"
        style={{
          width: 14,
          height: 14,
          transform: checked ? 'translateX(19px)' : 'translateX(3px)',
        }}
      />
    </button>
  );
}

// ─── Day toggle strip ──────────────────────────────────────────────────────────

function DayToggles({ dayVisibility, onToggle, updatingDay }) {
  return (
    <div
      className="rounded-2xl p-5 flex flex-col gap-4"
      style={{ backgroundColor: '#202020', border: '1px solid rgba(255,255,255,0.08)' }}
    >
      <div>
        <p className="font-poppins font-semibold text-white text-sm">Days shown on site</p>
        <p className="font-poppins text-white/40 text-xs mt-0.5">
          Turn a day off to hide all its events from the public site.
        </p>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex justify-center gap-4">
          {['Monday','Tuesday','Wednesday','Thursday','Friday'].map(day => {
            const on = dayVisibility[day] !== false;
            const isUpdating = updatingDay === day;
            return (
              <div key={day} className="flex flex-col items-center gap-1.5">
                <span
                  className="font-poppins font-semibold text-xs"
                  style={{ color: on ? '#F7941D' : 'rgba(255,255,255,0.3)' }}
                >
                  {DAY_SHORT[day]}
                </span>
                <Toggle
                  checked={on}
                  onChange={val => onToggle(day, val)}
                  disabled={isUpdating}
                />
              </div>
            );
          })}
        </div>
        <div className="flex justify-center gap-4">
          {['Saturday','Sunday'].map(day => {
            const on = dayVisibility[day] !== false;
            const isUpdating = updatingDay === day;
            return (
              <div key={day} className="flex flex-col items-center gap-1.5">
                <span
                  className="font-poppins font-semibold text-xs"
                  style={{ color: on ? '#F7941D' : 'rgba(255,255,255,0.3)' }}
                >
                  {DAY_SHORT[day]}
                </span>
                <Toggle
                  checked={on}
                  onChange={val => onToggle(day, val)}
                  disabled={isUpdating}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Time helpers ──────────────────────────────────────────────────────────────

const HOURS   = ['4','5','6','7','8','9','10','11'];
const MINUTES = ['00','15','30','45'];

function parseTime(timeStr = '') {
  const m = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return { hour: '7', minute: '00', period: 'PM' };
  return { hour: String(parseInt(m[1], 10)), minute: m[2], period: m[3].toUpperCase() };
}

function composeTime(hour, minute, period) {
  return `${hour}:${minute} ${period}`;
}

// ─── Event form ────────────────────────────────────────────────────────────────

const EMPTY_FORM = { day: 'Monday', hour: '7', minute: '00', period: 'PM', venue_id: '' };

function EventForm({ initial = EMPTY_FORM, venues, onSave, onCancel, onAddVenue, saving, saveStatus }) {
  const [form, setForm] = useState(initial);
  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleVenueChange = e => {
    if (e.target.value === '__add_venue__') {
      onAddVenue();
    } else {
      set('venue_id', e.target.value);
    }
  };

  const selectStyle = {
    backgroundColor: '#2A2A2A',
    border: '1px solid rgba(255,255,255,0.1)',
    color: form.venue_id || form.day ? '#fff' : 'rgba(255,255,255,0.3)',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 14px center',
    appearance: 'none',
    WebkitAppearance: 'none',
    paddingRight: 36,
  };

  return (
    <div
      className="rounded-2xl flex flex-col gap-5 p-6 mt-2"
      style={{ backgroundColor: '#252525', border: '1px solid rgba(247,148,29,0.25)' }}
    >
      {/* Day */}
      <div className="flex flex-col gap-1.5">
        <label className="font-poppins font-semibold text-white text-sm">Day</label>
        <select
          value={form.day}
          onChange={e => set('day', e.target.value)}
          className="font-poppins text-sm rounded-xl px-4 py-3 focus:outline-none transition-colors duration-200"
          style={selectStyle}
          onFocus={e => e.target.style.borderColor = 'rgba(247,148,29,0.5)'}
          onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
        >
          {DAYS.map(d => <option key={d} value={d} style={{ backgroundColor: '#2A2A2A' }}>{d}</option>)}
        </select>
      </div>

      {/* Time */}
      <div className="flex flex-col gap-1.5">
        <label className="font-poppins font-semibold text-white text-sm">Time</label>
        <div className="flex items-center gap-2">
          {/* Hour */}
          <select
            value={form.hour}
            onChange={e => set('hour', e.target.value)}
            className="font-poppins text-sm text-white rounded-xl px-4 py-3 focus:outline-none transition-colors duration-200 flex-1"
            style={{ ...selectStyle, color: '#fff' }}
            onFocus={e => e.target.style.borderColor = 'rgba(247,148,29,0.5)'}
            onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
          >
            {HOURS.map(h => <option key={h} value={h} style={{ backgroundColor: '#2A2A2A' }}>{h}</option>)}
          </select>
          <span className="font-poppins font-bold text-white/40 text-lg flex-shrink-0">:</span>
          {/* Minute */}
          <select
            value={form.minute}
            onChange={e => set('minute', e.target.value)}
            className="font-poppins text-sm text-white rounded-xl px-4 py-3 focus:outline-none transition-colors duration-200 flex-1"
            style={{ ...selectStyle, color: '#fff' }}
            onFocus={e => e.target.style.borderColor = 'rgba(247,148,29,0.5)'}
            onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
          >
            {MINUTES.map(m => <option key={m} value={m} style={{ backgroundColor: '#2A2A2A' }}>{m}</option>)}
          </select>
          {/* AM / PM */}
          <select
            value={form.period}
            onChange={e => set('period', e.target.value)}
            className="font-poppins text-sm text-white rounded-xl px-4 py-3 focus:outline-none transition-colors duration-200"
            style={{ ...selectStyle, color: '#fff', minWidth: 80 }}
            onFocus={e => e.target.style.borderColor = 'rgba(247,148,29,0.5)'}
            onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
          >
            <option value="AM" style={{ backgroundColor: '#2A2A2A' }}>AM</option>
            <option value="PM" style={{ backgroundColor: '#2A2A2A' }}>PM</option>
          </select>
        </div>
      </div>

      {/* Venue */}
      <div className="flex flex-col gap-1.5">
        <label className="font-poppins font-semibold text-white text-sm">Venue</label>
        {venues.length === 0 ? (
          <div
            className="rounded-xl px-4 py-3 flex items-center justify-between gap-3"
            style={{ backgroundColor: '#2A2A2A', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <p className="font-poppins text-sm" style={{ color: 'rgba(255,255,255,0.35)' }}>
              No venues yet —
            </p>
            <button
              type="button"
              onClick={onAddVenue}
              className="font-poppins text-sm font-semibold focus:outline-none"
              style={{ color: '#F7941D' }}
            >
              Add a venue first →
            </button>
          </div>
        ) : (
          <select
            value={form.venue_id}
            onChange={handleVenueChange}
            className="font-poppins text-sm rounded-xl px-4 py-3 focus:outline-none transition-colors duration-200"
            style={{ ...selectStyle, color: form.venue_id ? '#fff' : 'rgba(255,255,255,0.35)' }}
            onFocus={e => e.target.style.borderColor = 'rgba(247,148,29,0.5)'}
            onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
          >
            <option value="" disabled style={{ backgroundColor: '#2A2A2A' }}>Select a venue…</option>
            {venues.map(v => (
              <option key={v.id} value={v.id} style={{ backgroundColor: '#2A2A2A' }}>
                {v.name}{v.city ? ` — ${[v.city, v.state].filter(Boolean).join(', ')}` : ''}
              </option>
            ))}
            <option value="__add_venue__" style={{ backgroundColor: '#2A2A2A', color: '#F7941D' }}>
              + Add New Venue…
            </option>
          </select>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={() => onSave(form)}
          disabled={saving}
          className="font-poppins font-bold text-sm px-6 py-2.5 rounded-xl transition-all duration-200 focus:outline-none"
          style={{
            backgroundColor: saving ? 'rgba(247,148,29,0.4)' : '#F7941D',
            color: '#1A1A1A',
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
          onMouseEnter={e => { if (!saving) e.currentTarget.style.backgroundColor = '#ffb83f'; }}
          onMouseLeave={e => { if (!saving) e.currentTarget.style.backgroundColor = '#F7941D'; }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="font-poppins font-medium text-sm px-6 py-2.5 rounded-xl transition-all duration-200 focus:outline-none"
          style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
        >
          Cancel
        </button>

        {saveStatus === 'success' && (
          <div className="flex items-center gap-1.5">
            <span style={{ color: '#4ade80' }}>✓</span>
            <p className="font-poppins text-sm font-medium" style={{ color: '#4ade80' }}>Saved!</p>
          </div>
        )}
        {saveStatus === 'error' && (
          <p className="font-poppins text-sm font-medium" style={{ color: '#f87171' }}>
            Please fill in all fields.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Single event row ──────────────────────────────────────────────────────────

function EventRow({ entry, onEdit, onDelete }) {
  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl"
      style={{ backgroundColor: '#282828', border: '1px solid rgba(255,255,255,0.06)' }}
    >
      <div className="flex items-center gap-4 min-w-0">
        <span
          className="font-poppins font-bold text-sm flex-shrink-0"
          style={{ color: '#F7941D', minWidth: 64 }}
        >
          {entry.time}
        </span>
        <div className="min-w-0">
          <p className="font-poppins font-semibold text-white text-sm truncate">
            {entry.venues?.name ?? '—'}
          </p>
          {(entry.venues?.city || entry.venues?.state) && (
            <p className="font-poppins text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {[entry.venues.city, entry.venues.state].filter(Boolean).join(', ')}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={() => onEdit(entry)}
          className="font-poppins text-xs font-semibold px-4 py-2.5 rounded-lg transition-all duration-200 focus:outline-none"
          style={{ backgroundColor: 'rgba(247,148,29,0.1)', color: '#F7941D', border: '1px solid rgba(247,148,29,0.2)' }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(247,148,29,0.2)'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(247,148,29,0.1)'}
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => onDelete(entry)}
          className="font-poppins text-xs font-semibold px-4 py-2.5 rounded-lg transition-all duration-200 focus:outline-none"
          style={{ backgroundColor: 'rgba(248,113,113,0.08)', color: 'rgba(248,113,113,0.7)', border: '1px solid rgba(248,113,113,0.15)' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(248,113,113,0.18)'; e.currentTarget.style.color = '#f87171'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(248,113,113,0.08)'; e.currentTarget.style.color = 'rgba(248,113,113,0.7)'; }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// ─── Delete confirmation modal ─────────────────────────────────────────────────

function DeleteModal({ entry, onConfirm, onCancel, deleting }) {
  const label = entry
    ? `${entry.time} at ${entry.venues?.name ?? 'this venue'} (${entry.day})`
    : 'this event';
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
    >
      <div
        className="rounded-2xl p-7 flex flex-col gap-5 w-full max-w-sm"
        style={{ backgroundColor: '#202020', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        <div className="flex flex-col gap-2">
          <h2 className="font-poppins font-bold text-white text-lg">Delete event?</h2>
          <p className="font-poppins text-white/50 text-sm leading-relaxed">
            You're about to remove{' '}
            <span className="text-white font-semibold">{label}</span>{' '}
            from the schedule.
          </p>
          <p className="font-poppins text-white/40 text-sm">This cannot be undone.</p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="font-poppins font-bold text-sm px-6 py-2.5 rounded-xl focus:outline-none transition-all duration-200"
            style={{
              backgroundColor: deleting ? 'rgba(248,113,113,0.4)' : '#f87171',
              color: '#fff',
              cursor: deleting ? 'not-allowed' : 'pointer',
            }}
          >
            {deleting ? 'Deleting…' : 'Yes, delete it'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="font-poppins font-medium text-sm px-6 py-2.5 rounded-xl focus:outline-none transition-all duration-200"
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main section ──────────────────────────────────────────────────────────────

function ScheduleSection({ onNavigate }) {
  const [entries, setEntries]           = useState([]);
  const [venues, setVenues]             = useState([]);
  const [dayVisibility, setDayVisibility] = useState(
    Object.fromEntries(DAYS.map(d => [d, true]))
  );
  const [updatingDay, setUpdatingDay]   = useState(null);
  const [loading, setLoading]           = useState(true);
  const [editingId, setEditingId]       = useState(null); // null | 'new' | <id>
  const [editInitial, setEditInitial]   = useState(EMPTY_FORM);
  const [saving, setSaving]             = useState(false);
  const [saveStatus, setSaveStatus]     = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);
  const [openDays, setOpenDays]         = useState(new Set()); // all collapsed by default

  const toggleDayOpen = day =>
    setOpenDays(prev => {
      const next = new Set(prev);
      next.has(day) ? next.delete(day) : next.add(day);
      return next;
    });

  // ── Fetch schedule + venues ──────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [scheduleRes, venuesRes] = await Promise.all([
      supabase
        .from('schedule')
        .select('*, venues(id, name, city, state)')
        .order('day'),
      supabase
        .from('venues')
        .select('id, name, city, state')
        .order('name'),
    ]);

    if (!scheduleRes.error && scheduleRes.data) {
      setEntries(scheduleRes.data);
      // Derive day visibility from first seen entry per day (default true if no entries)
      const vis = Object.fromEntries(DAYS.map(d => [d, true]));
      scheduleRes.data.forEach(e => {
        // only override if explicitly false
        if (e.day_visible === false) vis[e.day] = false;
      });
      setDayVisibility(vis);
    }
    if (!venuesRes.error && venuesRes.data) setVenues(venuesRes.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Day visibility toggle ────────────────────────────────────────────────
  async function handleDayToggle(day, newVal) {
    setDayVisibility(prev => ({ ...prev, [day]: newVal }));
    setUpdatingDay(day);
    // Update all entries for this day
    await supabase
      .from('schedule')
      .update({ day_visible: newVal })
      .eq('day', day);
    setUpdatingDay(null);
  }

  // ── Open forms ───────────────────────────────────────────────────────────
  function handleEdit(entry) {
    const { hour, minute, period } = parseTime(entry.time);
    setEditInitial({
      day: entry.day,
      hour,
      minute,
      period,
      venue_id: String(entry.venue_id),
    });
    setEditingId(entry.id);
    setSaveStatus(null);
  }

  function handleAddNew() {
    setEditInitial(EMPTY_FORM);
    setEditingId('new');
    setSaveStatus(null);
  }

  function handleCancel() {
    setEditingId(null);
    setSaveStatus(null);
  }

  // ── Save ─────────────────────────────────────────────────────────────────
  async function handleSave(form) {
    if (!form.day || !form.venue_id) {
      setSaveStatus('error');
      return;
    }
    setSaving(true);
    setSaveStatus(null);

    const payload = {
      day: form.day,
      time: composeTime(form.hour, form.minute, form.period),
      venue_id: form.venue_id,
      day_visible: dayVisibility[form.day] !== false,
    };

    let error;
    if (editingId === 'new') {
      ({ error } = await supabase.from('schedule').insert(payload));
    } else {
      ({ error } = await supabase.from('schedule').update(payload).eq('id', editingId));
    }

    setSaving(false);
    if (error) {
      setSaveStatus('error');
    } else {
      setSaveStatus('success');
      await fetchAll();
      setTimeout(() => { setEditingId(null); setSaveStatus(null); }, 1200);
    }
  }

  // ── Delete ───────────────────────────────────────────────────────────────
  async function handleDeleteConfirm() {
    setDeleting(true);
    const { error } = await supabase.from('schedule').delete().eq('id', deleteTarget.id);
    setDeleting(false);
    if (!error) {
      setEntries(prev => prev.filter(e => e.id !== deleteTarget.id));
      if (editingId === deleteTarget.id) setEditingId(null);
    }
    setDeleteTarget(null);
  }

  // ── Group entries by day, sorted by time then venue ──────────────────────
  const grouped = DAYS.map(day => {
    const dayEntries = entries
      .filter(e => e.day === day)
      .sort((a, b) => {
        const timeDiff = timeToMinutes(a.time) - timeToMinutes(b.time);
        if (timeDiff !== 0) return timeDiff;
        return (a.venues?.name ?? '').localeCompare(b.venues?.name ?? '');
      });
    return { day, entries: dayEntries };
  }).filter(g => g.entries.length > 0);

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col gap-6 animate-pulse">
        <div className="h-7 w-40 rounded-lg bg-white/10" />
        <div className="h-24 rounded-2xl bg-white/5" />
        {[1, 2].map(i => <div key={i} className="h-32 rounded-2xl bg-white/5" />)}
      </div>
    );
  }

  const totalEvents = entries.length;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-6">

      {/* Header + Add button */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-poppins font-bold text-white text-2xl">Schedule</h1>
          <p className="font-poppins text-white/40 text-sm mt-1">
            {totalEvents} event{totalEvents !== 1 ? 's' : ''} across {grouped.length} day{grouped.length !== 1 ? 's' : ''}.
          </p>
        </div>
        {editingId !== 'new' && (
          <button
            type="button"
            onClick={handleAddNew}
            className="font-poppins font-bold text-sm px-5 py-2.5 rounded-xl transition-all duration-200 focus:outline-none flex items-center gap-2 flex-shrink-0"
            style={{ backgroundColor: '#F7941D', color: '#1A1A1A' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#ffb83f'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#F7941D'}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
            Add New Event
          </button>
        )}
      </div>

      {/* Day toggles */}
      <DayToggles
        dayVisibility={dayVisibility}
        onToggle={handleDayToggle}
        updatingDay={updatingDay}
      />

      {/* New event form */}
      {editingId === 'new' && (
        <EventForm
          initial={editInitial}
          venues={venues}
          onSave={handleSave}
          onCancel={handleCancel}
          onAddVenue={() => onNavigate('venues')}
          saving={saving}
          saveStatus={saveStatus}
        />
      )}

      {/* Grouped schedule list */}
      {grouped.length === 0 && editingId !== 'new' ? (
        <div
          className="rounded-2xl p-10 flex flex-col items-center justify-center gap-3"
          style={{ backgroundColor: '#202020', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <p className="font-poppins text-white/30 text-sm text-center">
            No events yet. Hit "Add New Event" to get started!
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {grouped.map(({ day, entries: dayEntries }) => {
            const isOpen    = openDays.has(day);
            const isVisible = dayVisibility[day] !== false;
            const isUpdating = updatingDay === day;
            return (
              <div
                key={day}
                className="rounded-2xl overflow-hidden"
                style={{ backgroundColor: '#202020', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                {/* ── Accordion header ── */}
                <button
                  type="button"
                  onClick={() => toggleDayOpen(day)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left focus:outline-none transition-colors duration-200"
                  style={{ backgroundColor: isOpen ? 'rgba(255,255,255,0.03)' : 'transparent' }}
                >
                  {/* Chevron */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="flex-shrink-0 transition-transform duration-250"
                    style={{ width: 16, height: 16, color: 'rgba(255,255,255,0.3)', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  >
                    <path fillRule="evenodd" d="M12.53 16.28a.75.75 0 01-1.06 0l-7.5-7.5a.75.75 0 011.06-1.06L12 14.69l6.97-6.97a.75.75 0 111.06 1.06l-7.5 7.5z" clipRule="evenodd" />
                  </svg>

                  {/* Day name */}
                  <span
                    className="font-poppins font-bold text-sm flex-1"
                    style={{ color: isVisible ? '#F7941D' : 'rgba(255,255,255,0.3)' }}
                  >
                    {day}
                  </span>

                  {/* Event count */}
                  <span
                    className="font-poppins text-xs px-2 py-0.5 rounded-full mr-2"
                    style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.35)' }}
                  >
                    {dayEntries.length} event{dayEntries.length !== 1 ? 's' : ''}
                  </span>

                  {/* Hidden badge */}
                  {!isVisible && (
                    <span
                      className="font-poppins text-xs px-2 py-0.5 rounded-full mr-2"
                      style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.3)' }}
                    >
                      hidden
                    </span>
                  )}

                  {/* Day visibility toggle — stop propagation so it doesn't open/close the accordion */}
                  <div onClick={e => e.stopPropagation()}>
                    <Toggle
                      checked={isVisible}
                      onChange={val => handleDayToggle(day, val)}
                      disabled={isUpdating}
                    />
                  </div>
                </button>

                {/* ── Expandable content ── */}
                {isOpen && (
                  <div
                    className="flex flex-col gap-2 px-4 pb-4 pt-1"
                    style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
                  >
                    {dayEntries.map(entry => (
                      <div key={entry.id}>
                        <EventRow
                          entry={entry}
                          onEdit={handleEdit}
                          onDelete={e => setDeleteTarget(e)}
                        />
                        {editingId === entry.id && (
                          <EventForm
                            initial={editInitial}
                            venues={venues}
                            onSave={handleSave}
                            onCancel={handleCancel}
                            onAddVenue={() => onNavigate('venues')}
                            saving={saving}
                            saveStatus={saveStatus}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete modal */}
      {deleteTarget && (
        <DeleteModal
          entry={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          deleting={deleting}
        />
      )}

    </div>
  );
}

export default ScheduleSection;
